import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable, of } from "rxjs";
import { concatMap, map, switchMap, tap } from 'rxjs/operators';
import { ValeurListeFixeModel } from 'src/app/models/listes-fixes/valeur-liste-fixe.model';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { FileDownloaderService } from '../../common/file-downloader.service';
import { UploadUtilService } from '../../common/photo-uploader/upload-util.service';
import { PersonnelModel } from '../../models/personnels/personnel.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { FileConversionsUtil, base64string } from '../../utils/file-conversions-util';
import { ConfigService } from '../config/config.service';
import { IndividusService } from '../individus/individus.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';
import { UtilisateurCourantService } from '../utilisateurs/utilisateur-courant.service';


@Injectable({
  providedIn: 'root',
})
export class PersonnelsService {
  private readonly baseUrlApi = this.configService.config.urlApi;;

  private get personnelUrl() {
    return `${this.baseUrlApi}/api/crm/personnels`;
  }

  constructor(
    private oDataAdaptor: ODataAdaptor,
    private client: HttpClient,
    private fileDownloaderService: FileDownloaderService,
    private notificationToastService: NotificationToastService,
    private uploadUtilService: UploadUtilService,
    private individusService: IndividusService,
    private utilisateurCourantService: UtilisateurCourantService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  buildDataManagerGetPersonnels(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/crm/PersonnelsView`,
      adaptor: this.oDataAdaptor,
    });
  }

  addPersonnel(personnel: PersonnelModel): Observable<number> {
    return this.individusService.getNewGuid().pipe(
      tap(value => (personnel.individuGuid = value)),
      concatMap(() => this.addPersonnelWithGuid(personnel)),
    );
  }

  private addPersonnelWithGuid(personnel: PersonnelModel): Observable<number> {
    return this.client
      .put<number>(`${this.personnelUrl}/addpersonnel`, personnel)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Le personnel a bien été créé.'))));
  }

  updatePersonnel(personnel: PersonnelModel): Observable<number> {
    return this.client.post(`${this.personnelUrl}/updatePersonnel`, personnel).pipe(
      tap((idPersonnel: number) => {
        this.notificationToastService.push(new ToastSuccesModel('Le personnel a bien été modifié.'));
        return idPersonnel;
      }),
    );
  }

  getPersonnelDetaille(idPersonnel: number): Observable<PersonnelModel> {
    return this.client.get<PersonnelModel>(`${this.personnelUrl}/getpersonnel/${idPersonnel}`).pipe(
      map(p => {
        if (p.etatCivil.dateDeNaissance != null) {
          p.etatCivil.dateDeNaissance = new Date(p.etatCivil.dateDeNaissance);
        }
        return p;
      }),
    );
  }

  televerserPhoto(photo: File, idIndividu: number) {
    return this.uploadUtilService.uploadFile(`${this.personnelUrl}/DefinirPhoto`, {File: photo, idIndividu})
      .pipe(
        tap(() => this.notificationToastService.push(new ToastSuccesModel(`Fichier téléversé avec succès`)))
      );
  }
  supprimerPhoto(idPersonnel: number): Observable<void> {
    return this.client.delete<void>(`${this.personnelUrl}/DeletePhoto/${idPersonnel}`)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel(`Photo supprimée avec succès`))))
  }

  chargerPhotoAsBase64(idPersonnel: number): Observable<base64string> {
    return this.fileDownloaderService.fetchFile(`${this.baseUrlApi}/api/crm/IndividusPhoto/${idPersonnel}`, null)
      .pipe(switchMap((blob: Blob) => {
        if (blob.size == 0) {
          return of(null)
        } else {
          return FileConversionsUtil.imageBlobToBase64(blob);
        }
      }))
  }

  getPersonnelDetailleByGuid(guid: string): Observable<PersonnelModel> {
    return this.client.get<PersonnelModel>(`${this.personnelUrl}/GetPersonnelByGuid/${guid}`);
  }

  activerDesactiverPersonnel(personnel: PersonnelModel, activer: boolean) {
    const statusText = activer === true ? 'activée' : 'désactivée';
    return this.client
      .post(`${this.personnelUrl}/toggleActivationPersonnel`, {
        id: personnel.id,
        actif: activer,
      })
      .pipe(
        tap(() => (personnel.actif = activer)),
        tap(() => this.notificationToastService.push(new ToastSuccesModel(`Le personnel a été ${statusText}`))),
      );
  }

  getPersonnelConnecte(): Observable<PersonnelModel> {
    return this.getPersonnelDetailleByGuid(this.utilisateurCourantService.getGuidUtilisateur());
  }

  getSelectionPersonnelsUrl(): string {
    return `${this.configService.config.urlApi}/api/f/personnelsSelectionView`;
  }

  public getStates(): Observable<ValeurListeFixeModel[]>{
    return this.client.get<ValeurListeFixeModel[]>(`${this.configService.config.urlApi}/api/crm/ListeFixeEtatsPersonnel/getValeurs`);
  }
}
